import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { APIService } from 'app/services/api.service';
import { environment } from 'environments/environment';
import { Store } from "@ngrx/store";
import { selectTokenInfo } from "app/reducers/context.selectors";
import { TokenInfo } from "app/reducers/context.reducer";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { refreshLoginData, signOutUser } from "app/reducers/context.actions";
import { InterceptorHelper } from 'app/services/http/interceptor-helper';
import { ApiAuthRefreshUrl, ApiSignalUrl } from 'app/services/http/url-constants/hub-url-constants';

const Unauthenticated = "Unauthenticated"

// Alternative POS backend hostnames the user can pick in the GetPOS modal
// (BaseUrl.enum of schema-modal-geco-pos.component.ts). They all back the same
// JWT issuer as environment.posBaseUrl, so we must attach the bearer token for
// them too. Without this whitelist the very first request to any of these
// hosts goes out without Authorization, gets a 401 from the backend, and only
// after a forced /refresh + retry inside the interceptor does it succeed —
// which on production triggers a recursive reinit loop in @ajsf.
const KNOWN_POS_HOSTS = [
  'https://api-pos.me.ge-co.io',
  'https://api-pos.eu.ge-co.io',
  'https://api-pos.ru.ge-co.io',
  'https://pos-staging.open-s.info',
  'https://api-kiosk.getit.rest',
  'http://api-pos.ge-co.local',
];

@Injectable({
  providedIn: 'root',
})
@UntilDestroy()
export class TokenInterceptorService implements HttpInterceptor {
  private tokenInfo: TokenInfo | null = null;

  constructor(
    private apiService: APIService,
    private store: Store,
  ) {
    this.store.select(selectTokenInfo).pipe(
      untilDestroyed(this)
    ).subscribe(tokenInfo => {
      this.tokenInfo = tokenInfo
    })
  }

  intercept(
    req: HttpRequest<object>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const isOurDomain = (req.url.includes(environment.baseUrl)
        || req.url.includes(environment.posBaseUrl)
        || req.url.includes(environment.kioskBaseUrl)
        || req.url.includes(environment.kioskVPNBaseUrl)
        // Каталоги прав ЦУ и QR-меню для матрицы ролей проверяют View Roles по токену хаба.
        // На части стендов адреса пустые — пустая строка совпала бы с любым url.
        || (!!environment.kioskCcApiUrl && req.url.includes(environment.kioskCcApiUrl))
        || (!!environment.qrMenuBaseUrl && req.url.includes(environment.qrMenuBaseUrl))
        || KNOWN_POS_HOSTS.some(host => req.url.includes(host)))
      && req.url.indexOf(ApiSignalUrl) === -1;

    if (!isOurDomain) {
      return this.handleWithErrorPipe(req, next);
    }

    // Read current tokenInfo from the store synchronously instead of relying on
    // this.tokenInfo which is populated via an async subscribe in the ctor —
    // that subscribe may not have fired yet when the very first HTTP request
    // goes through the interceptor (race condition on app boot / page reload).
    return this.store.select(selectTokenInfo).pipe(
      take(1),
      switchMap(tokenInfo => {
        const authedReq = tokenInfo
          ? this.addToken(req, tokenInfo.token)
          : req;
        return this.handleWithErrorPipe(authedReq, next);
      })
    );
  }

  private handleWithErrorPipe(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((err, event) => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          if (err.url.includes(ApiAuthRefreshUrl)) {
            throw new Error(Unauthenticated);
          }
          return this.refreshTokenHandle(req, next);
        }
        throw err;
      }),
      switchMap((event) => {
        if (event instanceof HttpResponse) {
          if (InterceptorHelper.IsJsonRpc(event) && InterceptorHelper.NoAuthorizeJsonRpc(event)) {
            return this.refreshTokenHandle(req, next);
          }
        }
        return of(event);
      }),
      catchError((err, event) => {
        if (err.message == Unauthenticated) {
          this.store.dispatch(signOutUser());
        }
        throw err;
      })
    );
  }

  private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({setHeaders: {Authorization: `Bearer ${token}`}});
  }

  private refreshTokenHandle(
    req: HttpRequest<object>,
    next: HttpHandler
  ): Observable<HttpEvent<any>>
  {
    if (!this.tokenInfo?.refreshToken){
      throw new Error(Unauthenticated);
    }

    return this.apiService.refreshToken({
      refreshToken: this.tokenInfo.refreshToken
    }).pipe(
      switchMap(loginResponse => {
        if (!loginResponse?.data?.refreshToken){
          throw new Error(Unauthenticated);
        }

        const newToken = loginResponse.data.authToken;
        const tokenParts = this.apiService.decodeToken(loginResponse.data.authToken);

        this.store.dispatch(refreshLoginData({
          tokenInfo: {
            token: loginResponse.data.authToken,
            refreshToken: loginResponse.data.refreshToken,
            tokenExpires: tokenParts.tokenExpires,
          },
          userData: loginResponse.data.userData
        }));
        return next.handle(this.addToken(req, newToken));
      }),
      catchError(err => {
        throw new Error(Unauthenticated)
      })
    )
  }
}
